import React from "react";

// Marcas combinantes geradas pelo NFD (acentos, til, cedilha...).
const COMBINING_MARKS = /[\u0300-\u036f]/g;

// Conta apenas letras/números (ignora espaços, pontuação e símbolos). Usado para
// decidir quando uma busca é específica o bastante para valer a consulta.
export const meaningfulLength = (value) =>
  (String(value || "").match(/[\p{L}\p{N}]/gu) || []).length;

// "Dobra" o texto para comparação: minúsculo e sem acento. Espelha a collation
// utf8mb4_unicode_ci da coluna Messages.body, que é quem decide o match no LIKE
// do backend — sem isso, buscar "ciencia" traz "ciência" mas não realça nada.
export const foldText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase();

// Atalho para ASCII (a esmagadora maioria dos caracteres): não tem acento, então
// evita o custo do normalize() por caractere em corpos longos.
const foldChar = (ch) =>
  ch.charCodeAt(0) < 128 ? ch.toLowerCase() : foldText(ch);

// Versão dobrada do texto + mapa de cada posição dobrada de volta para o índice
// no texto original. Um caractere pode virar 0, 1 ou mais posições depois da
// dobra, por isso o mapa é montado caractere a caractere (Array.from preserva
// emojis/surrogates inteiros). A sentinela final fecha o último intervalo.
const foldWithIndex = (text) => {
  const chars = Array.from(text);
  const map = [];
  let folded = "";

  chars.forEach((ch, index) => {
    const piece = foldChar(ch);
    for (let k = 0; k < piece.length; k += 1) map.push(index);
    folded += piece;
  });
  map.push(chars.length);

  return { chars, folded, map };
};

const CONTEXT_BEFORE = 40;
const CONTEXT_AFTER = 100;
const FALLBACK_LENGTH = 160;
const MAX_MATCHES = 50;

/**
 * Constrói um trecho curto centrado na primeira ocorrência do termo, com todas
 * as ocorrências visíveis destacadas. A comparação ignora acento e caixa, igual
 * ao banco. Compartilhado pelo drawer de busca dentro do ticket e pela seção
 * "Mensagens" da busca agrupada da lista de atendimentos.
 */
export const buildHighlightedSnippet = (body, term, highlightClass) => {
  const text = String(body || "");
  if (!term) return text;

  const foldedTerm = foldText(term);
  const { chars, folded, map } = foldWithIndex(text);

  // Intervalos em coordenadas de `chars` (índices do texto original).
  const matches = [];
  let searchFrom = 0;
  while (foldedTerm && matches.length < MAX_MATCHES) {
    const at = folded.indexOf(foldedTerm, searchFrom);
    if (at === -1) break;
    matches.push([map[at], map[at + foldedTerm.length]]);
    searchFrom = at + foldedTerm.length;
  }

  if (matches.length === 0) {
    // Sem match no corpo (ex.: match veio de campo não exibido) — mostra início.
    return chars.length > FALLBACK_LENGTH
      ? `${chars.slice(0, FALLBACK_LENGTH).join("")}…`
      : text;
  }

  const start = Math.max(0, matches[0][0] - CONTEXT_BEFORE);
  const end = Math.min(chars.length, matches[0][1] + CONTEXT_AFTER);

  const nodes = [];
  let cursor = start;

  matches.forEach(([from, to]) => {
    if (to <= start || from >= end) return;
    const hlStart = Math.max(from, start);
    const hlEnd = Math.min(to, end);
    if (hlStart > cursor) {
      nodes.push(
        <React.Fragment key={`t-${cursor}`}>
          {chars.slice(cursor, hlStart).join("")}
        </React.Fragment>
      );
    }
    nodes.push(
      <span key={`h-${hlStart}`} className={highlightClass}>
        {chars.slice(hlStart, hlEnd).join("")}
      </span>
    );
    cursor = hlEnd;
  });

  if (cursor < end) {
    nodes.push(
      <React.Fragment key={`t-${cursor}`}>
        {chars.slice(cursor, end).join("")}
      </React.Fragment>
    );
  }

  return (
    <>
      {start > 0 ? "…" : ""}
      {nodes}
      {end < chars.length ? "…" : ""}
    </>
  );
};

export default buildHighlightedSnippet;
